import React, { useEffect, useRef, useState } from "react";
import styles from "./PocketCard.module.css";
import clsx from "clsx";
import ArrowButton from "../../ArrowButton";
import AssetsPack from "../../../../../../util/AssetsPack";
import { HitlMessage } from "../../../../../../api/responsePayload/HitlMessageResponse";
import { MessageStatus } from "../../../../dataModel/ChatBotDataModel";
import {
  onChatbotActions,
  postHitlMessageApiCall,
} from "../../../../../../store/chatbot.slice";
import { CHAT_MEMBER_ID } from "../../../CreateFlow";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../store/common/hooks";

export enum PocketCardStates {
  Initial,
  Loading,
  Content,
}
interface PocketCardProps extends React.HTMLAttributes<HTMLDivElement> {
  state?: PocketCardStates;
}

const PocketCard: React.FC<PocketCardProps> = ({
  state = PocketCardStates.Initial,
  ...divProps
}) => {
  const [editFlags, setEditFlags] = useState<Record<string, boolean>>({});
  const [disableAction, setDisableAction] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const [aiMessageList, setAiMessageList] = useState<HitlMessage[]>([]);
  const reduxAiMessageList = useAppSelector(
    (state) => state.chatbot.aiMessageList
  );
  const dispatch = useAppDispatch();
  const toggleEditMode = (messageId: string, index: number) => {
    const isEditing = editFlags[messageId];
    if (isEditing) {
      // Cancel: reset revised_message
      const updatedList = [...aiMessageList];
      updatedList[index] = {
        ...updatedList[index],
        revised_message: null,
      };
      setAiMessageList(updatedList);
    }

    setEditFlags((prev) => ({
      ...prev,
      [messageId]: !isEditing,
    }));
  };

  const closeEditMode = (messageId: string) => {
    setEditFlags((prev) => ({
      ...prev,
      [messageId]: false,
    }));
  };

  const scrollTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth", // 🔥 smooth scroll
      });
    }
  };

  useEffect(() => {
    setAiMessageList(reduxAiMessageList);
    scrollTop();
  }, [reduxAiMessageList]);

  useEffect(() => {
    scrollTop();
  }, [state]);

  const hitlApiCall = async (
    message: string,
    messageStatus: MessageStatus,
    messageId?: string
  ) => {
    setDisableAction(true);
    dispatch(
      postHitlMessageApiCall(message, CHAT_MEMBER_ID, messageStatus, messageId)
    ).then(() => {
      setDisableAction(false);
    });

    if (messageId) {
      if (messageStatus == MessageStatus.Changed) {
        closeEditMode(messageId);
      }

      dispatch(
        onChatbotActions.updateAiMessageStatus({
          messageId,
          newStatus: messageStatus,
        })
      );
    }
  };

  const getCardClassNames = (status: MessageStatus) => {
    return clsx(
      status === MessageStatus.Approve && styles["approved"],
      status === MessageStatus.Rejected && styles["rejected"]
    );
  };

  const handleMessageChange = (index: number, newValue: string) => {
    const updatedList = [...aiMessageList];
    updatedList[index] = {
      ...updatedList[index],
      revised_message: newValue,
    };
    setAiMessageList(updatedList);
    dispatch(onChatbotActions.setAiMessageList(updatedList));
  };

  return (
    <div
      {...divProps}
      className={clsx(
        styles["jap-container-card-main"],
        styles["smartcontext"],
        divProps.className
      )}
      ref={containerRef}
    >
      <div className={styles["jap-container-inner-scroll"]}>
        {state == PocketCardStates.Loading && (
          <div className={styles["jap-generated-card-container"]}>
            <div className={styles["jap-header-container"]}>
              <div
                className={clsx(
                  styles["jap-container-heading-pill-gradient-outer"],
                  styles["smartcontext"]
                )}
              >
                <div className={styles["jap-container-heading-pill-inner"]}>
                  <p>Updating SmartContext</p>
                </div>
              </div>
            </div>
            <div className={styles["loader-container"]}>
              <div
                className={clsx(styles["loading-bar"], styles["line01"])}
              ></div>
              <div
                className={clsx(styles["loading-bar"], styles["line02"])}
              ></div>
              <div
                className={clsx(styles["loading-bar"], styles["line03"])}
              ></div>
              <div
                className={clsx(styles["loading-bar"], styles["line04"])}
              ></div>
            </div>
            <div className={styles["jap-actions-bar"]}>
              <div className={styles["jap-button-container"]}>
                <div className={styles["action-left-half"]}>
                  <button
                    className={clsx(
                      styles["jap-button"],
                      styles["jap-btn-edit"]
                    )}
                  >
                    Edit
                  </button>
                  <button
                    className={clsx(
                      styles["jap-button"],
                      styles["jap-btn-delete"]
                    )}
                  >
                    Delete
                  </button>
                </div>
                <div className={styles["action-right-half"]}>
                  <ArrowButton />
                </div>
              </div>
            </div>
          </div>
        )}
        {aiMessageList.map((data, index) => (
          <div
            key={index}
            className={clsx(
              styles["jap-generated-card-container"],
              styles["jap-active"],
              getCardClassNames(data.status)
            )}
          >
            <div className={styles["jap-header-container"]}>
              <div className={styles["jap-timestamp"]}>
                {" "}
                <img
                  className={styles["jap-logo"]}
                  src={AssetsPack.icons.ICON_JOKER_AI.default}
                  alt=""
                />
                <p>{data.date_created}</p>
              </div>
            </div>
            <div className={styles["jap-generated-answer-text"]}>
              {editFlags[data.message_id ?? ""] ? (
                <textarea
                  value={data.revised_message ?? data.ai_message}
                  onChange={(e) => handleMessageChange(index, e.target.value)}
                  className={clsx(
                    styles["qc-textarea"],
                    styles["qc-editing-mode"]
                  )}
                />
              ) : (
                <p>
                  {data.status == MessageStatus.Changed
                    ? data.revised_message
                    : data.ai_message}
                </p>
              )}
            </div>
            <div className={styles["jap-actions-bar"]}>
              {data.status == 3 && (
                <div className={styles["jap-button-container"]}>
                  <div className={styles["action-left-half"]}>
                    <button
                      className={clsx(
                        styles["jap-button"],
                        styles["jap-btn-edit"]
                      )}
                      onClick={() => {
                        if (!disableAction)
                          toggleEditMode(data.message_id ?? "", index);
                      }}
                    >
                      {editFlags[data.message_id ?? ""] ? "Cancel" : "Edit"}
                    </button>
                    {!editFlags[data.message_id ?? ""] && (
                      <button
                        className={clsx(
                          styles["jap-button"],
                          styles["jap-btn-delete"]
                        )}
                        onClick={() => {
                          if (!disableAction) {
                            hitlApiCall(
                              data.ai_message,
                              MessageStatus.Rejected,
                              data.message_id
                            );
                          }
                        }}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                  <div className={styles["action-right-half"]}>
                    <ArrowButton
                      onClick={() => {
                        if (!disableAction) {
                          hitlApiCall(
                            editFlags[data.message_id ?? ""]
                              ? data.revised_message ?? ""
                              : data.ai_message,
                            editFlags[data.message_id ?? ""]
                              ? MessageStatus.Changed
                              : MessageStatus.Approve,
                            data.message_id
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PocketCard;
